import { useEffect, useState, useContext } from "react";
import { HorizontalLayout, VerticalLayout } from "./layout";
import { deleteAPI, getAPI, postAPI } from "../api/api";
import { ButtonGroup, StandardButton } from "../atoms/buttons";
import { icon } from "../functions/icons";
import { Modal, ModalBody, ModalTitle } from "./modal";
import { InputForm } from "../atoms/input";
import { AddDepartmentModal } from "./customModals";
import { Alert } from "./alert";
import { SettingsHeader } from "../molecule/settingsHeader";
import { UserContext } from "../App";
import { Spinner } from "../atoms/spinner";

export const DepartmentSettings = () => {
  const [departments, setDepartments] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [removeModal, setRemoveModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userInformation = useContext(UserContext)
  const isPublic = userInformation.account_type === 1

  useEffect(() => {
    fetchUserDepartments();
  }, []);

  const fetchUserDepartments = async () => {
    setLoading(true);
    try {
      const response = await getAPI("api/business/departments");

      if (response.response_type === "error") {
        setDepartments([]);
      } else {
        setDepartments(response.data || []);
      }
    } catch (error) {
      setDepartments([]);
    }
    setLoading(false);
  };

  const toggleAddModal = () => {
    setAddModal(!addModal);
    setErrorMessage("");
  };

  const toggleEditModal = (department) => {
    console.log(department)
    setSelectedDepartment(department);
    setNewDepartmentName(department.department_name);
    setEditModal(!editModal);
  };

  const toggleRemoveModal = (department) => {
    setSelectedDepartment(department);
    setRemoveModal(!removeModal);
  };

  const handleRemoveDepartment = async () => {
    if (selectedDepartment) {
      try {
        const response = await deleteAPI("api/business/departments/remove", {
          department_id: selectedDepartment.id_department,
        });
  
        if (response.status === "ok") {
          fetchUserDepartments();
          setRemoveModal(false);
        } else {
          setErrorMessage(response.message || `Fout bij verwijderen ${isPublic ? "doelgroep" : "afdeling"}.`);
          setTimeout(() => setErrorMessage(""), 2000);
        }
      } catch (error) {
        console.error(`Fout bij verwijderen ${isPublic ? "doelgroep" : "afdeling"}.:`, error); 
        setErrorMessage(`Fout bij verwijderen  ${isPublic ? "doelgroep" : "afdeling"}..`);
        setTimeout(() => setErrorMessage(""), 2000);
      }
    }
  };

  const handleEditDepartment = async () => {
    if (!newDepartmentName.trim()) {
      setErrorMessage(`${isPublic ? "Doelgroep" : "Afdeling"} kan niet leeg zijn.`);
      setTimeout(() => setErrorMessage(""), 2000);  
      return;
    }
  
    try {
      const response = await postAPI("api/business/departments/edit", {
        department_id: selectedDepartment.id_department,
        new_department_name: newDepartmentName.trim(),
      });
  
      if (response.status === "ok") {
        fetchUserDepartments();
        setEditModal(false);
      } else {
        setErrorMessage(response.message || `Fout bij bewerken ${isPublic ? "doelgroep" : "afdeling"}.`);
        setTimeout(() => setErrorMessage(""), 2000); 
      }
    } catch (error) {
      console.error(`Fout bij bewerken ${isPublic ? "doelgroep" : "afdeling"}.:`, error); 
      setErrorMessage(`Deze ${isPublic ? "doelgroep" : "afdeling"} bestaat al`);
      setTimeout(() => setErrorMessage(""), 2000); 
    }
  };

  return (
    <VerticalLayout className="settings-card">

      <SettingsHeader
        title={isPublic ? "Doelgroepsbeheer" : "Afdelingenbeheer"}
        description={`Bekijk en beheer je ${isPublic ? "doelgroepen" : "afdelingen"}`}
        toggleAddModal={toggleAddModal}
      />

      <VerticalLayout className="settings-rows" style={{ margin: "0rem", marginTop: "unset", padding:"1rem", paddingTop:0 }} >
        <VerticalLayout
           style={{
            boxSizing: "border-box",
            borderColor: "var(--border-color)",
            borderRadius: "0.5rem",
            borderWidth:"1px",
            borderStyle: "solid",
            overflow:"hidden",
            maxHeight: "34rem", 
            overflowY: "auto", 
          }}
        >
          {isLoading ? (
            <Spinner />
          ) : departments.length > 0 ? (
            departments.map((department) => (
              <DepartmentComp
                key={department.id_department}
                editDepartment={toggleEditModal}
                department={department}
                removeDepartment={toggleRemoveModal}
              />
            ))
          ) : (
            <p className="standard-padding">Geen {isPublic ? "doelgroepen" : "afdelingen"} gekoppeld.</p>
          )}
        </VerticalLayout>
      </VerticalLayout>
      <AddDepartmentModal dismiss={toggleAddModal} visible={addModal}  fetchUserDepartments={fetchUserDepartments} />
      <DynamicModal
        visible={editModal}
        dismiss={() => setEditModal(false)}
        title={`${isPublic ? "Doelgroep" : "Afdeling"} bewerken`}
        content={
          <>
            <InputForm
              value={newDepartmentName}
              typeInput={setNewDepartmentName} 
              placeholder={`Nieuwe ${isPublic ? "doelgroepsnaam" : "afdelingsnaam"}`}
            />
           {errorMessage && (
              <Alert
                  marginTop={"1rem"}
                  type={"error"}
                  message={errorMessage}
              />
           )}
          </>
        }
        actions={{
          cancel: () => setEditModal(false),
          submit: handleEditDepartment,
          submitTitle: "Opslaan",
          disabled: isSubmitting,
        }}
      />
      <DynamicModal
        visible={removeModal}
        dismiss={() => setRemoveModal(false)}
        title={`${isPublic ? "Doelgroep" : "Afdeling"} verwijderen`}
        content={
          <>
            <p>
              Weet je zeker dat je de {isPublic ? "doelgroep" : "afdeling"}{" "}
              <strong>{selectedDepartment?.department_name}</strong> wilt
              verwijderen?
            </p>
            {errorMessage && (
                            <Alert
                                marginTop={"1rem"}
                                type={"error"}
                                message={errorMessage}
                            />
            )}
          </>
        }
        actions={{
          cancel: () => setRemoveModal(false),
          submit: handleRemoveDepartment,
          submitTitle: "Verwijderen",
        }}
      />
    </VerticalLayout>
  );
};

const DepartmentComp = ({ editDepartment, department, removeDepartment }) => {
  return (
    <div className="employee-comp">
      <div style={{ flexGrow: 1, display: "flex", flexDirection: "row" }}>
        <p className="employee-name" style={{ display: "flex", gap: "0.5rem" }}>
          {department.department_name}
        </p>
      </div>
      <div className="employee-actions" style={{ textAlign: "right" }}>
        <ButtonGroup
          buttons={[
            {
              id: "edit01",
              onClick: () => editDepartment(department),
              content: <>{icon("edit")}</>,
            },
            {
              id: "remove01",
              onClick: () => removeDepartment(department),
              content: <>{icon("trash")}</>,
            },
          ]}
        />
      </div>
    </div>
  );
};

const DynamicModal = ({ visible, dismiss, title, content, actions }) => (
  <Modal
    visible={visible}
    dismissable={dismiss}
    content={
      <>
        <ModalTitle title={title} hasXmark dismissable={dismiss} />
        <ModalBody>
          {content}
          <ModalActions
            onCancel={actions.cancel}
            onSubmit={actions.submit}
            submitTitle={actions.submitTitle}
            disabled={actions.disabled}
          />
        </ModalBody>
      </>
    }
  />
);

const ModalActions = ({ onCancel, onSubmit, submitTitle, disabled }) => (
  <HorizontalLayout className="space-between standard-margin-top">
    <StandardButton onClick={onCancel} title="Annuleren" />
    <StandardButton
      onClick={onSubmit}
      title={submitTitle}
      disabled={disabled}
    />
  </HorizontalLayout>
);
